import { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

function MultiChartPage({ user, setUser }) {
  const [assets, setAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [chartData, setChartData] = useState({});

  useEffect(() => {
    fetchAssets();
  }, []);

  useEffect(() => {
    if (selectedAssets.length > 0) {
      generateChartData();
    }
  }, [selectedAssets]);

  const fetchAssets = async () => {
    try {
      const response = await axios.get(`${API}/assets`);
      // Show Turkish stocks: VIOP, BIST_100, etc.
      const turkishAssets = response.data.filter(a => 
        a.category === 'VIOP' || 
        a.category === 'BIST_100' || 
        a.category === 'BORSA_ISTANBUL'
      ).slice(0, 30);
      setAssets(turkishAssets);
      
      // Auto-select first 4
      if (turkishAssets.length > 0) {
        setSelectedAssets(turkishAssets.slice(0, 4));
      }
    } catch (err) {
      console.error('Assets fetch error:', err);
    }
  };

  const generateChartData = () => {
    const data = {};
    selectedAssets.forEach(asset => {
      const points = [];
      const basePrice = asset.current_price;
      for (let i = 0; i < 30; i++) {
        const variance = (Math.random() - 0.5) * basePrice * 0.03;
        points.push({
          time: i,
          price: basePrice + variance
        });
      }
      data[asset.id] = points;
    });
    setChartData(data);
  };

  const toggleAsset = (asset) => {
    if (selectedAssets.find(a => a.id === asset.id)) {
      setSelectedAssets(selectedAssets.filter(a => a.id !== asset.id));
    } else if (selectedAssets.length < 4) {
      setSelectedAssets([...selectedAssets, asset]);
    } else {
      alert('❌ Maksimum 4 varlık seçebilirsiniz');
    }
  };

  const colors = ['#8B5CF6', '#10B981', '#F59E0B', '#EF4444'];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar user={user} setUser={setUser} />
      
      <div className="max-w-7xl mx-auto p-4">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">📊 Çoklu Grafik Görünümü</h1>
          <p className="text-gray-400 text-sm sm:text-base">Birden fazla varlığı aynı anda takip edin (Maksimum 4)</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Asset Selector */}
          <div className="lg:col-span-1 bg-gray-800 rounded-lg p-4 max-h-[600px] overflow-y-auto">
            <h3 className="font-bold mb-4 text-sm sm:text-base">Varlık Seç</h3>
            <div className="space-y-2">
              {assets.map(asset => (
                <button
                  key={asset.id}
                  onClick={() => toggleAsset(asset)}
                  className={`w-full text-left p-3 rounded transition-colors ${
                    selectedAssets.find(a => a.id === asset.id)
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-sm">{asset.symbol}</div>
                      <div className="text-xs text-gray-400 truncate">{asset.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-mono">₺{asset.current_price.toFixed(2)}</div>
                      <div className={`text-xs ${
                        asset.change_percent >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {asset.change_percent >= 0 ? '+' : ''}{asset.change_percent.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Charts Grid */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {selectedAssets.map((asset, idx) => (
              <div key={asset.id} className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-bold text-base sm:text-lg" style={{ color: colors[idx] }}>
                      {asset.symbol}
                    </div>
                    <div className="text-xs text-gray-400">{asset.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg sm:text-xl font-bold" style={{ color: colors[idx] }}>
                      ₺{asset.current_price.toFixed(2)}
                    </div>
                    <div className={`text-sm ${
                      asset.change_percent >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {asset.change_percent >= 0 ? '+' : ''}{asset.change_percent.toFixed(2)}%
                    </div>
                  </div>
                </div>

                {/* Simple Chart */}
                <div className="relative" style={{ height: '200px' }}>
                  <svg width="100%" height="100%" viewBox="0 0 300 200" preserveAspectRatio="none">
                    {/* Grid */}
                    <g stroke="#374151" strokeWidth="0.5">
                      {[0, 1, 2, 3, 4].map(i => (
                        <line key={`h-${i}`} x1="0" y1={i * 50} x2="300" y2={i * 50} />
                      ))}
                    </g>
                    
                    {/* Line */}
                    {chartData[asset.id] && chartData[asset.id].length > 0 && (
                      <>
                        {/* Area fill */}
                        <path
                          d={`M 0 200 ${chartData[asset.id].map((d, i) => {
                            const x = (i / (chartData[asset.id].length - 1)) * 300;
                            const minPrice = Math.min(...chartData[asset.id].map(p => p.price));
                            const maxPrice = Math.max(...chartData[asset.id].map(p => p.price));
                            const y = 200 - ((d.price - minPrice) / (maxPrice - minPrice)) * 180;
                            return `L ${x} ${y}`;
                          }).join(' ')} L 300 200 Z`}
                          fill={colors[idx]}
                          fillOpacity="0.1"
                        />
                        
                        {/* Price line */}
                        <polyline
                          points={chartData[asset.id].map((d, i) => {
                            const x = (i / (chartData[asset.id].length - 1)) * 300;
                            const minPrice = Math.min(...chartData[asset.id].map(p => p.price));
                            const maxPrice = Math.max(...chartData[asset.id].map(p => p.price));
                            const y = 200 - ((d.price - minPrice) / (maxPrice - minPrice)) * 180;
                            return `${x},${y}`;
                          }).join(' ')}
                          fill="none"
                          stroke={colors[idx]}
                          strokeWidth="2"
                        />
                      </>
                    )}
                  </svg>
                </div>
              </div>
            ))}

            {/* Empty slots */}
            {[...Array(4 - selectedAssets.length)].map((_, idx) => (
              <div key={`empty-${idx}`} className="bg-gray-800 rounded-lg p-4 flex items-center justify-center h-[280px] border-2 border-dashed border-gray-700">
                <div className="text-center text-gray-500">
                  <div className="text-3xl mb-2">📈</div>
                  <div className="text-sm">Varlık seçin</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MultiChartPage;
